import React from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { Box, Typography } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { GoalTargetIcon } from '@components/icons';
import { SectionIndices } from '../../../common/i-doc/i-doc.registry.ts';
import { clientGoalCard as c } from '@/styles/tokens';
import { ClientMainGoal } from '@services/client-main-goal.service';

const labelSx = { fontSize: 16, lineHeight: '20px', color: c.textMuted } as const;

const WeightValue: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
    <Typography sx={labelSx}>{label}</Typography>
    <Typography sx={{ fontSize: 18, lineHeight: '24px', fontWeight: 500, color: c.text }}>{value}</Typography>
  </Box>
);

// Weight goal (Figma "Goal Card", weight variant): current / target weight and progress from the start weight.
const WeightGoal: React.FC<{ start: number; current: number; target: number }> = ({ start, current, target }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'clientProfile.mainGoal' });
  const total = Math.abs(target - start);
  const remaining = Math.max(0, (target - current) * Math.sign(target - start));
  const progress = total ? Math.min(100, Math.max(0, ((total - remaining) / total) * 100)) : 100;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex' }}>
        <WeightValue label={t('currentWeight')} value={t('weightValue', { value: current })} />
        <WeightValue label={t('targetWeight')} value={t('weightValue', { value: target })} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ position: 'relative', height: 6, borderRadius: '4px', bgcolor: c.progressTrack }}>
          <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${progress}%`, borderRadius: '4px', bgcolor: c.progressFill }} />
        </Box>
        <Typography sx={{ fontSize: 14, lineHeight: '17px', color: c.textMuted }}>
          {remaining > 0 ? t('remaining', { value: remaining, start }) : t('reached', { start })}
        </Typography>
      </Box>
    </Box>
  );
};

interface ClientMainGoalCardProps {
  /** null - клієнт ще не створив жодної цілі (`GET /client/:clientId/main-goal`). */
  goal: ClientMainGoal | null;
}

/**
 * Client's main goal card (Figma "Goal Card"): desired result or weight goal - реальні дані
 * (`UserGoal`, модуль `goals`), 2026-10-29. Вагова ціль (`kind === 'quantitative'`) показує
 * прогресбар лише коли всі три значення (старт/поточне/ціль) задані - інакше, як і для якісної
 * цілі без `desiredResult`, картка показує стан "Результат ще не уточнено" (той самий стан, що
 * й у моб. додатку, `goal-card.component.ts`: `state: 'undefined'` - без кнопки "Уточнити", це
 * read-only віджет).
 */
export const ClientMainGoalCard: React.FC<ClientMainGoalCardProps> = ({ goal }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'clientProfile.mainGoal' });
  const isWeight = !!goal && goal.kind === 'quantitative'
    && goal.startValue != null && goal.currentValue != null && goal.targetValue != null;

  return (
    <Box
      data-i-doc={SectionIndices.EXPERT_CLIENT_MAIN_GOAL}
      sx={{
        bgcolor: c.background, boxShadow: c.shadow, borderRadius: '12px', p: isWeight ? 2.5 : 2,
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: isWeight ? 2 : 3.5,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 44, height: 44, p: 1, borderRadius: '8px', bgcolor: c.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <GoalTargetIcon sx={{ fontSize: 28, color: c.icon }} />
          </Box>
          <Typography sx={{ fontSize: 16, lineHeight: '19px', fontWeight: 500, fontVariant: 'all-small-caps', color: c.label }}>
            {t('label')}
          </Typography>
        </Box>

        {goal ? (
          <>
            <Typography sx={{ fontSize: isWeight ? 20 : 18, lineHeight: isWeight ? '24px' : '21px', fontWeight: 500, color: c.title }}>
              {t(`type.${goal.typeCode}`)}
            </Typography>
            {goal.targetDate && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', px: 1, py: '6px', border: `1px solid ${c.chipBorder}`, borderRadius: '8px' }}>
                <CalendarMonthOutlinedIcon sx={{ fontSize: 20, color: c.chipText }} />
                <Typography sx={{ fontSize: 15, lineHeight: '20px', color: c.chipText }}>
                  {t('until', { date: format(parseISO(goal.targetDate), 'dd.MM.yyyy') })}
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <Typography sx={labelSx}>{t('empty')}</Typography>
        )}
      </Box>

      {isWeight ? (
        <WeightGoal start={goal!.startValue!} current={goal!.currentValue!} target={goal!.targetValue!} />
      ) : goal?.desiredResult ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={labelSx}>{t('desiredResult')}</Typography>
          <Typography sx={{ fontSize: 16, lineHeight: '24px', color: c.text }}>{goal.desiredResult}</Typography>
        </Box>
      ) : goal ? (
        <Typography sx={labelSx}>{t('resultUndefined')}</Typography>
      ) : null}
    </Box>
  );
};
